import {
  AssistAnswerChoice,
  HearAndConfirmChoice,
  IHearAndConfirmBody,
} from '../types';

/**
 * HITL-1780 (HITL 8.0.0): the hear-and-confirm card's pickable transcripts,
 * ported from MiniHITL's `correction.ts` to the schema 1.0 body. Pure: no
 * lane, ring, reviewer or socket imports.
 */

/** One pickable transcript on the card; its position + 1 is its key. */
export interface CardOption {
  choice: Extract<HearAndConfirmChoice, 'primary' | 'secondary' | 'alt'>;
  /** Already normalised by gpt-context: what the card shows and sends. */
  text: string;
  label: string;
  /** Position in `primary.nbest`, for `alt`. */
  altIndex?: number;
}

/** The contract caps `nbest` at 3; kept as a guard against a larger list. */
export const MAX_ALTERNATIVES = 3;

const ENGINE_LABEL: Record<string, string> = {
  azure: 'Azure',
  assemblyai: 'AssemblyAI',
};

/** `engine` is a label only (contract §5.1): nothing else branches on it. */
export const engineLabel = (engine?: string) => {
  const key = (engine || '').trim();
  if (!key) return 'Unknown';
  return (
    ENGINE_LABEL[key.toLowerCase()] ??
    key.charAt(0).toUpperCase() + key.slice(1)
  );
};

export const normalizeTranscript = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9$%' ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Primary, secondary, then the primary's alternatives, with an alternative
 * dropped when it reads the same as an option above it. A fixed order: key 1
 * is always the primary and key 2 the secondary, whichever the AI went with —
 * the card does not mark `winner`, so the agent is not nudged towards it.
 */
export const buildCardOptions = (body: IHearAndConfirmBody): CardOption[] => {
  const options: CardOption[] = [
    {
      choice: 'primary',
      text: (body.primary?.text || '').trim(),
      label: engineLabel(body.primary?.engine),
    },
    {
      choice: 'secondary',
      text: (body.secondary?.text || '').trim(),
      label: engineLabel(body.secondary?.engine),
    },
  ];
  const seen = new Set(options.map((o) => normalizeTranscript(o.text)));
  const altLabel = `${engineLabel(body.primary?.engine)} alternative`;
  (body.primary?.nbest || []).forEach((alt, index) => {
    const text = (alt || '').trim();
    const key = normalizeTranscript(text);
    if (!key || seen.has(key) || options.length >= 2 + MAX_ALTERNATIVES) {
      return;
    }
    seen.add(key);
    options.push({ choice: 'alt', text, label: altLabel, altIndex: index });
  });
  return options;
};

export interface IOptionAnswer {
  answerChoice: AssistAnswerChoice;
  answerText: string;
}

/**
 * What `sendAnswer` takes for a picked option. An alternative is named by its
 * place in `primary.nbest`, 1-based (`alt_1` is `nbest[0]`), even when an
 * earlier entry was dropped as a duplicate.
 */
export const answerForOption = (option: CardOption): IOptionAnswer => ({
  answerChoice:
    option.choice === 'alt'
      ? (`alt_${(option.altIndex ?? 0) + 1}` as AssistAnswerChoice)
      : option.choice,
  answerText: option.text,
});

/** A typed answer that reads exactly like an option counts as that option. */
export const choiceForTypedText = (
  typed: string,
  options: CardOption[]
): { choice: HearAndConfirmChoice; option?: CardOption } => {
  const key = normalizeTranscript(typed);
  const option = options.find((o) => normalizeTranscript(o.text) === key);
  return option ? { choice: option.choice, option } : { choice: 'edited' };
};

/**
 * Word-level marks for the words of `a` that are not in the longest common
 * subsequence with `b`: what this engine heard that the other did not.
 * Computed here rather than read from `diff.tokens`, which only pairs the
 * primary with the secondary, so the alternatives can be marked too.
 */
export const diffMarks = (
  a: string,
  b: string
): { word: string; differs: boolean }[] => {
  const A = a.split(/\s+/).filter(Boolean);
  const B = b.split(/\s+/).filter(Boolean);
  const na = A.map((w) => w.toLowerCase().replace(/[^a-z0-9]/g, ''));
  const nb = B.map((w) => w.toLowerCase().replace(/[^a-z0-9]/g, ''));
  const lcs: number[][] = Array.from({ length: A.length + 1 }, () =>
    new Array(B.length + 1).fill(0)
  );
  for (let i = A.length - 1; i >= 0; i -= 1) {
    for (let j = B.length - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        na[i] === nb[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const differs = new Array(A.length).fill(true);
  let i = 0;
  let j = 0;
  while (i < A.length && j < B.length) {
    if (na[i] === nb[j]) {
      differs[i] = false;
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }
  return A.map((word, k) => ({ word, differs: differs[k] }));
};

const LISTEN_FOR: Record<string, string> = {
  numbers: 'Listen for the number',
  negation: 'Listen for no / with',
};

/** The hint line from `diff.reason`; null for a reason the card has no hint for. */
export const listenForHint = (reason?: string): string | null =>
  (reason && LISTEN_FOR[reason]) || null;
